import React, { useEffect, useState } from "react";
import { Cpu, Plus, Edit3, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "",
  gpu_model: "",
  core_offset: 0,
  mem_offset: 0,
  core_clock: 0,
  mem_clock: 0,
  power_limit: 0,
  fan_speed: 0,
};

const NUM_FIELDS = [
  { k: "core_clock", label: "Core Clock", unit: "MHz (0=def)" },
  { k: "mem_clock", label: "Memory Clock", unit: "MHz (0=def)" },
  { k: "core_offset", label: "Core Offset", unit: "MHz" },
  { k: "mem_offset", label: "Memory Offset", unit: "MHz" },
  { k: "power_limit", label: "Power Limit", unit: "W (0=def)" },
  { k: "fan_speed", label: "Fan Speed", unit: "% (0=auto)" },
];

function FormModal({ open, onClose, initial, onSaved }) {
  const [data, setData] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setData(initial || EMPTY);
  }, [open, initial]);

  if (!open) return null;

  const update = (k, v) => setData((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!data.name || !data.name.trim()) {
      toast.error("Il nome del profilo è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: data.name.trim(),
        gpu_model: data.gpu_model || "",
      };
      NUM_FIELDS.forEach(({ k }) => (payload[k] = Number(data[k] || 0)));
      if (data.id) {
        await api.put(`/oc-profiles/${data.id}`, payload);
      } else {
        await api.post("/oc-profiles", payload);
      }
      toast.success(data.id ? "Profilo aggiornato" : "Profilo creato");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bento p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ocp-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl">
            {data.id ? "Modifica Profilo OC" : "Nuovo Profilo OC"}
          </h3>
          <button onClick={onClose} data-testid="ocp-modal-close" className="text-[#A1A1AA] hover:text-[#FAFAFA]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          {[
            { k: "name", label: "Nome", placeholder: "Es: RTX 3060 Warthog" },
            { k: "gpu_model", label: "Modello GPU (opzionale)", placeholder: "RTX 3060" },
          ].map(({ k, label, placeholder }) => (
            <label key={k} className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
                {label}
              </span>
              <input
                value={data[k]}
                onChange={(e) => update(k, e.target.value)}
                placeholder={placeholder}
                data-testid={`ocp-input-${k}`}
                className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
              />
            </label>
          ))}
          <div className="grid grid-cols-2 gap-3">
            {NUM_FIELDS.map(({ k, label, unit }) => (
              <label key={k} className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] block mb-1.5">
                  {label} <span className="text-[#52525B]">{unit}</span>
                </span>
                <input
                  type="number"
                  value={data[k]}
                  onChange={(e) => update(k, e.target.value)}
                  data-testid={`ocp-input-${k}`}
                  className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
                />
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-[#A1A1AA] hover:text-[#FAFAFA]"
          >
            Annulla
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="ocp-save-btn"
            className="bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OcProfilesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/oc-profiles");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!confirm(`Eliminare il profilo OC "${p.name}"?`)) return;
    try {
      await api.delete(`/oc-profiles/${p.id}`);
      toast.success("Profilo eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
            // Overclock Presets
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            OC Profiles
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Libreria condivisa di preset overclock. Applicabili a qualsiasi rig o singola GPU dal dettaglio rig.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="ocp-new-btn"
          className="flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em]"
        >
          <Plus className="w-4 h-4" /> Nuovo
        </button>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-[#A1A1AA]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bento p-12 text-center">
          <Cpu className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-bold text-xl mb-2">Nessun profilo OC</h3>
          <p className="text-sm text-[#A1A1AA] mb-5">
            Crea il primo preset (es. RTX 3060 Warthog) per riutilizzarlo su più rig.
          </p>
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="ocp-empty-new-btn"
            className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-bold rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em]"
          >
            <Plus className="w-4 h-4" /> Crea Profilo OC
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-fade">
          {items.map((p) => (
            <div key={p.id} className="bento p-5" data-testid={`ocp-card-${p.name}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{p.name}</div>
                  {p.gpu_model && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#10B981] mt-0.5">
                      {p.gpu_model}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditing(p); setOpen(true); }}
                    data-testid={`ocp-edit-${p.name}`}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#27272A] rounded-sm"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`ocp-delete-${p.name}`}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
                {NUM_FIELDS.map(({ k, label }) => (
                  <div key={k} className="flex items-baseline justify-between">
                    <span className="text-[#A1A1AA] text-[10px] uppercase tracking-[0.1em]">{label}</span>
                    <span className="text-[#FAFAFA]">{p[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <FormModal open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
